import {
  shouldValidateEndTime,
  shouldValidateStartTime,
  validateEndTime,
  validateStartTime,
} from 'utils/util'
import { array, bool, date, number, string } from 'yup'

const timeRegex = /^(0?[1-9]|1[0-2]):[0-5]\d (AM|PM)$/
const emptyDateValues = new Set([
  '',
  'MM/DD/YYYY',
  'mm/dd/yyyy',
  'Invalid Date',
])
const emptyTimeValues = new Set([
  '',
  'Invalid Date',
  'hh:mm aa',
  'hh:mm AM/PM',
  '__:__ __',
])

const normalizeDateInput = (value: unknown) => {
  if (typeof value !== 'string') {
    return value
  }

  const trimmedValue = value.trim()
  return emptyDateValues.has(trimmedValue) ? '' : trimmedValue
}

const normalizeTimeInput = (value: unknown) => {
  if (typeof value !== 'string') {
    return value
  }

  const trimmedValue = value.trim()
  return emptyTimeValues.has(trimmedValue) ? '' : trimmedValue
}

export const badgeRewardCategorySchema = string()

export const badgeRewardNameSchema = string()
  .required('Must enter a badge reward name')
  .max(50)

export const badgeRewardDescriptionSchema = string()
  .required('Must enter a badge reward description')
  .max(500)

export const badgeRewardTypeSchema = string().oneOf(
  [
    'CheckIn',
    'CheckInStreak',
    'PatronProfile_Address',
    'PatronProfile_Anniversary',
    'Referral_Count',
    'PatronProfile_Birthday',
    'UniqueLocationVisits',
  ],
  'Badge Reward Type is not valid'
)

export const locationBadgeTypeSchema = string().oneOf(
  ['LocationCheckIn', 'LocationCheckInStreak', 'UniqueLocationVisits'],
  'Badge Reward Type is not valid'
)

export const badgeRewardSubtypeSchema = string()
  .oneOf(
    ['Location', 'CombinedLocations', 'CorporateAccount', 'Organization'],
    'Badge Reward Subtype is not valid'
  )
  .required('Must enter a badge reward subtype')

const isLocationBasedBadgeSubtype = (subtype?: string) => subtype === 'Location'

export const badgeRewardLicensedEstablishmentIdsSchema = array()
  .of(number())
  .transform((value, originalValue) =>
    Array.isArray(originalValue) && originalValue.length === 0 ? null : value
  )
  .when('subtype', {
    is: (subtype: string) => isLocationBasedBadgeSubtype(subtype),
    then: () =>
      array()
        .of(number())
        .min(1, 'Must select at least one Licensed Establishment')
        .required('Must select at least one Licensed Establishment'),
    otherwise: () => array().of(number()).nullable(),
  })

export const badgeRewardCorporateAccountIdSchema = number().when('subtype', {
  is: (subtype: string) => subtype === 'CorporateAccount',
  then: () => number().required('Must select a Corporate Account'),
  otherwise: () => number().nullable(),
})

export const badgeRewardOrganizationIdSchema = number().when('subtype', {
  is: (subtype: string) => subtype === 'Organization',
  then: () => number().required('Must select an Organization'),
  otherwise: () => number().nullable(),
})

export const badgeRewardJJPointsAwardedSchema = number()

export const badgeRewardCountSchema = number().min(
  1,
  'Count must be at least 1'
)

export const badgeRewardScheduledSchema = array()

export const badgeRewardRenewableTypeSchema = string().oneOf(
  [
    'No',
    'DaysInARow',
    'Daily',
    'Weekly',
    'Monthly',
    'Quarterly',
    'Yearly',
    'CheckInStreak',
    'LocationCheckInStreak',
  ],
  'Badge Reward Renewable Type is not valid'
)

export const newBadgeRewardStartDateSchema = date()
  .typeError('Must enter a valid start date')
  .transform((currentValue, originalValue, context) => {
    if (context.isType(currentValue)) {
      return currentValue
    }
    if (originalValue === '') {
      return null
    }
    if (originalValue === 'MM/DD/YYYY') {
      return null
    }
    return currentValue
  })

export const newBadgeRewardStartTimeSchema = string().when(
  ['startDate'],
  ([startDate], schema) =>
    schema
      .test({
        name: 'startTimeFormat',
        message: 'Must enter a valid start time.',
        test: (value) => !value || timeRegex.test(value),
      })
      .test({
        name: 'startTime',
        message: 'Start time should not be in the past.',
        test: (value) =>
          !value || !timeRegex.test(value)
            ? true
            : shouldValidateStartTime(startDate, value)
              ? validateStartTime(startDate, value)
              : true,
      })
)

export const newBadgeRewardEndDateSchema = date()
  .nullable()
  .typeError('Must enter a valid end date')
  .transform((currentValue, originalValue, context) => {
    if (context.isType(currentValue)) {
      return currentValue
    }
    if (originalValue === '') {
      return null
    }
    if (originalValue === 'MM/DD/YYYY') {
      return null
    }
    return currentValue
  })
  .when(['startDate'], ([startDate], schema) => {
    if (startDate instanceof Date && Number.isFinite(+startDate)) {
      return schema.min(startDate, 'End date should not be before start date')
    }
    return schema
  })

export const newBadgeRewardEndTimeSchema = string().when(
  ['startDate', 'startTime', 'endDate'],
  ([startDate, startTime, endDate], schema) => {
    return schema.test({
      name: 'endTime',
      message: 'This field is required',
      test: (value) => {
        const timeRegex = /^(0?[1-9]|1[0-2]):[0-5]\d (AM|PM)$/
        if (
          value &&
          endDate instanceof Date &&
          Number.isFinite(+endDate) &&
          !timeRegex.test(value)
        ) {
          return false
        }

        if (endDate instanceof Date && value?.toString().length === 0) {
          return false
        }

        if (value && endDate instanceof Date && Number.isFinite(+endDate)) {
          shouldValidateEndTime(startDate, startTime, endDate, value) &&
            validateEndTime(startDate, startTime, endDate, value)
        }

        return true
      },
    })
  }
)

export const editBadgeRewardStartDateSchema = date()
  .typeError('Must enter a valid start date')
  .transform((currentValue, originalValue, context) => {
    const normalizedOriginalValue = normalizeDateInput(originalValue)

    if (context.isType(currentValue)) {
      return currentValue
    }
    if (normalizedOriginalValue === '') {
      return null
    }
    return currentValue
  })
  .test({
    name: 'startDateRequiredWhenStartTimePresent',
    message: 'Start date is required when start time is provided',
    test: function (value) {
      const normalizedStartTime = normalizeTimeInput(this.parent.startTime)

      if (!normalizedStartTime) {
        return true
      }

      return value instanceof Date && Number.isFinite(+value)
    },
  })

export const editBadgeRewardStartTimeSchema = string()
  .transform((currentValue, originalValue) => {
    const normalizedOriginalValue = normalizeTimeInput(originalValue)

    return normalizedOriginalValue === '' ? '' : currentValue
  })
  .test({
    name: 'startTimeRequiredWhenStartDatePresent',
    message: 'Start time is required when start date is provided',
    test: function (value) {
      const startDate = this.parent.startDate

      if (!(startDate instanceof Date) || !Number.isFinite(+startDate)) {
        return true
      }

      return normalizeTimeInput(value) !== ''
    },
  })
  .test({
    name: 'startTime',
    message: 'Must enter a valid start time.',
    test: (value) => {
      const normalizedValue = normalizeTimeInput(value)

      if (!normalizedValue) {
        return true
      }

      return (
        typeof normalizedValue === 'string' && timeRegex.test(normalizedValue)
      )
    },
  })

export const editBadgeRewardEndDateSchema = date()
  .nullable()
  .typeError('Must enter a valid end date')
  .transform((currentValue, originalValue, context) => {
    const normalizedOriginalValue = normalizeDateInput(originalValue)

    if (context.isType(currentValue)) {
      return currentValue
    }
    if (normalizedOriginalValue === '') {
      return null
    }
    return currentValue
  })
  .when(['startDate'], ([startDate], schema) => {
    if (startDate instanceof Date && Number.isFinite(+startDate)) {
      return schema.min(startDate, 'End date should not be before start date')
    }
    return schema
  })

export const editBadgeRewardEndTimeSchema = string().when(
  ['startDate', 'startTime', 'endDate'],
  ([startDate, startTime, endDate], schema) => {
    return schema.test({
      name: 'endTime',
      message: 'End time is required when end date is provided',
      test: (value) => {
        if (
          value &&
          endDate instanceof Date &&
          Number.isFinite(+endDate) &&
          !timeRegex.test(value)
        ) {
          return false
        }

        if (endDate instanceof Date && value?.toString().length === 0) {
          return false
        }

        if (value && endDate instanceof Date && Number.isFinite(+endDate)) {
          shouldValidateEndTime(startDate, startTime, endDate, value) &&
            validateEndTime(startDate, startTime, endDate, value)
        }

        return true
      },
    })
  }
)

export const badgeRewardSchedulesSchema = array()

export const badgeRewardDependsOnBadgeRewardIdSchema = number()
  .nullable()
  .typeError('Must select a valid badge reward')

export const badgeRewardLimitCheckinsSchema = bool()
export const badgeRewardsProgramId = number()
  .nullable()
  .transform((currentValue, originalValue) =>
    originalValue === '' || Number.isNaN(currentValue) ? null : currentValue
  )
